import { Box, IconButton, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ReactNode } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

export function FormSection({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 2.5,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
        sx={{ mb: 2.5 }}
      >
        <Stack gap={0.5} sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.3 }}>
            {title}
          </Typography>
          {description ? (
            <Typography color="text.secondary" sx={{ fontSize: 13.5, lineHeight: 1.5 }}>
              {description}
            </Typography>
          ) : null}
        </Stack>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

export function ChoiceTile({
  selected,
  label,
  description,
  onClick,
}: {
  selected: boolean;
  label: string;
  description?: string;
  onClick: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      sx={{
        appearance: "none",
        font: "inherit",
        color: "inherit",
        textAlign: "start",
        flex: 1,
        minWidth: 140,
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: (theme) =>
          selected ? alpha(theme.palette.primary.main, 0.07) : "background.paper",
        cursor: "pointer",
        transition: "border-color 120ms ease, background-color 120ms ease",
        "&:hover": {
          borderColor: selected ? "primary.main" : "text.secondary",
        },
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 14.5 }}>{label}</Typography>
      {description ? (
        <Typography color="text.secondary" sx={{ fontSize: 12.5, mt: 0.5 }}>
          {description}
        </Typography>
      ) : null}
    </Box>
  );
}

export function LinkedRow({
  primary,
  secondary,
  onRemove,
  removeLabel,
}: {
  primary: string;
  secondary?: string;
  onRemove: () => void;
  removeLabel: string;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={1.5}
      sx={{
        py: 1.25,
        px: 1.75,
        borderRadius: 1.5,
        bgcolor: "grey.50",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
          {primary}
        </Typography>
        {secondary ? (
          <Typography color="text.secondary" sx={{ fontSize: 13 }} noWrap>
            {secondary}
          </Typography>
        ) : null}
      </Stack>
      <IconButton size="small" color="error" onClick={onRemove} aria-label={removeLabel}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}
